<template>
  <!-- Top 3-px red accent stripe -->
  <div class="nb-accent-bar" />

  <v-app-bar
    elevation="0"
    height="72"
    class="app-navbar"
    :style="navbarStyle"
  >
    <!-- Logo -->
    <router-link to="/" class="nb-logo ml-2 ml-md-3">
      <img src="@/assets/images/mediumlogo3-dark.png" alt="Striglia Enterprises" class="nb-logo-img" />
    </router-link>

    <v-spacer />

    <!-- ── Desktop navigation ── -->
    <nav class="d-none d-md-flex align-center nb-links mr-3">

      <!-- Regular links -->
      <router-link
        v-for="item in mainNavItems"
        :key="item.title"
        :to="item.to"
        class="nb-link"
        :class="{ 'nb-link--active': $route.path === item.to }"
      >
        {{ item.title }}
        <span class="nb-link-bar" />
      </router-link>

      <!-- Tools mega-menu -->
      <v-menu
        open-on-hover
        :close-on-content-click="true"
        location="bottom center"
        offset="12"
        transition="slide-y-transition"
      >
        <template #activator="{ props, isActive }">
          <button
            v-bind="props"
            class="nb-link"
            :class="{ 'nb-link--active': isToolsActive }"
          >
            Tools
            <v-icon
              size="15"
              class="nb-chevron"
              :class="{ 'nb-chevron--open': isActive }"
            >mdi-chevron-down</v-icon>
            <span class="nb-link-bar" />
          </button>
        </template>

        <!-- Mega dropdown card -->
        <div class="nb-mega">
          <div class="nb-mega-header">
            <div class="nb-mega-header-icon">
              <v-icon color="white" size="18">mdi-tools</v-icon>
            </div>
            <div>
              <div class="nb-mega-title">Tools &amp; Utilities</div>
              <div class="nb-mega-subtitle">Productivity tools for your business</div>
            </div>
          </div>
          <div class="nb-mega-grid">
            <router-link
              v-for="tool in toolItems"
              :key="tool.title"
              :to="tool.to"
              class="nb-tool-item"
            >
              <div class="nb-tool-icon">
                <v-icon :icon="tool.icon" size="20" color="white" />
              </div>
              <div class="nb-tool-text">
                <div class="nb-tool-name">{{ tool.title }}</div>
                <div class="nb-tool-desc">{{ tool.desc }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </v-menu>

      <!-- Secondary links -->
      <router-link
        v-for="item in secondaryNavItems"
        :key="item.title"
        :to="item.to"
        class="nb-link"
        :class="{ 'nb-link--active': $route.path === item.to }"
      >
        {{ item.title }}
        <span class="nb-link-bar" />
      </router-link>

      <!-- Vertical divider -->
      <div class="nb-divider" />

      <!-- Login / Logout button -->
      <button class="nb-auth-btn" :class="{ 'nb-auth-btn--out': isLoggedIn }" @click="handleLoginClick">
        <span class="nb-auth-icon">
          <v-icon size="16">{{ isLoggedIn ? 'mdi-logout-variant' : 'mdi-login-variant' }}</v-icon>
        </span>
        <span>{{ loginText }}</span>
      </button>
    </nav>

    <!-- Mobile hamburger — top RIGHT -->
    <v-app-bar-nav-icon
      class="d-flex d-md-none mr-1"
      color="white"
      @click="drawer = !drawer"
    />
  </v-app-bar>

  <!-- ── Login Dialog — Teleport to body for full-viewport centering ── -->
  <Teleport to="body">
    <Transition name="dlg-fade">
      <div v-if="showLoginDialog" class="dlg-overlay">
        <div class="dlg-box">
          <div class="ld-header">
            <v-icon color="white" size="36" class="ld-icon">mdi-lock-outline</v-icon>
            <div class="ld-title">Login</div>
            <div class="ld-subtitle">Sign in to your account</div>
          </div>
          <div class="ld-body">
            <v-text-field
              v-model="loginForm.username"
              label="Username"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-account-outline"
              :color="loginForm.username ? '#5e221d' : undefined"
              autocomplete="off"
              @keyup.enter="submitNavLogin"
            />
            <v-text-field
              v-model="loginForm.password"
              label="Password"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-lock-outline"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              :color="loginForm.password ? '#5e221d' : undefined"
              autocomplete="off"
              @click:append-inner="showPassword = !showPassword"
              @keyup.enter="submitNavLogin"
            />
            <v-alert
              v-if="navLoginError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-1 mb-0"
              border="start"
            >
              {{ navLoginError }}
            </v-alert>
          </div>
          <div class="ld-actions">
            <button
              class="ld-btn ld-btn-cancel"
              @click="showLoginDialog = false; navLoginError = ''; showPassword = false"
            >
              Cancel
            </button>
            <button
              class="ld-btn ld-btn-login"
              :class="{ 'ld-btn-disabled': !canSubmitNavLogin || navLoginLoading }"
              :disabled="!canSubmitNavLogin || navLoginLoading"
              @click="submitNavLogin"
            >
              <span v-if="navLoginLoading" class="ld-spinner" />
              <v-icon v-else size="18" class="mr-1">mdi-login</v-icon>
              {{ navLoginLoading ? 'Signing in…' : 'Login' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- ── Mobile dropdown panel (right side, below hamburger) ── -->
  <Transition name="dr-slide">
    <div
      v-if="drawer"
      class="dr-panel"
      v-click-outside="() => drawer = false"
    >
      <div class="dr-nav">
        <div class="dr-section-label">Navigation</div>

        <router-link
          v-for="item in mainNavItems"
          :key="item.title"
          :to="item.to"
          class="dr-link"
          :class="{ 'dr-link--active': $route.path === item.to }"
          @click="drawer = false"
        >
          <v-icon size="16" class="mr-3" color="#5e221d">{{ item.icon }}</v-icon>
          {{ item.title }}
        </router-link>

        <div class="dr-section-label mt-2">Tools</div>
        <router-link
          v-for="tool in toolItems"
          :key="tool.title"
          :to="tool.to"
          class="dr-link dr-link-tool"
          :class="{ 'dr-link--active': $route.path === tool.to }"
          @click="drawer = false"
        >
          <div class="dr-tool-icon">
            <v-icon size="13" color="white">{{ tool.icon }}</v-icon>
          </div>
          {{ tool.title }}
        </router-link>

        <div class="dr-section-label mt-2">Company</div>
        <router-link
          v-for="item in secondaryNavItems"
          :key="item.title"
          :to="item.to"
          class="dr-link"
          :class="{ 'dr-link--active': $route.path === item.to }"
          @click="drawer = false"
        >
          <v-icon size="16" class="mr-3" color="#5e221d">{{ item.icon }}</v-icon>
          {{ item.title }}
        </router-link>

        <div class="dr-divider" />

        <button
          class="dr-auth-btn"
          :class="{ 'dr-auth-btn--out': isLoggedIn }"
          @click="handleLoginClick(); drawer = false"
        >
          <v-icon size="16" class="mr-2">{{ isLoggedIn ? 'mdi-logout-variant' : 'mdi-login-variant' }}</v-icon>
          {{ loginText }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'AppNavBar',
  data() {
    return {
      drawer: false,
      scrolled: false,
      isLoggedIn: false,
      showLoginDialog: false,
      loginForm: { username: '', password: '' },
      navLoginError: '',
      navLoginLoading: false,
      showPassword: false,
      mainNavItems: [
        { title: 'Home', to: '/', icon: 'mdi-home-outline' },
        { title: 'Services', to: '/services', icon: 'mdi-cog-outline' }
      ],
      toolItems: [
        { title: 'Invoice Payment', to: '/tools/invoice-payment', icon: 'mdi-credit-card-outline', desc: 'Pay invoices securely online' },
        { title: 'Password Generator', to: '/tools/password-generator', icon: 'mdi-key-variant', desc: 'Create strong passwords' },
        { title: 'Support Agent', to: '/tools/support-agent', icon: 'mdi-headset', desc: 'Download remote support agent' },
        { title: 'Material Cutting', to: '/tools/material-cutting', icon: 'mdi-ruler-square', desc: 'Optimize cutting patterns' },
        { title: 'Upload Data', to: '/tools/upload-data', icon: 'mdi-upload', desc: 'Import XLSX data files' }
      ],
      secondaryNavItems: [
        { title: 'About', to: '/about', icon: 'mdi-information-outline' },
        { title: 'Support', to: '/support', icon: 'mdi-lifebuoy' },
        { title: 'Contact', to: '/contact', icon: 'mdi-email-outline' }
      ]
    };
  },
  computed: {
    loginText() { return this.isLoggedIn ? 'Logout' : 'Login'; },
    isToolsActive() { return this.$route.path.startsWith('/tools'); },
    canSubmitNavLogin() {
      return this.loginForm.username.trim().length > 0 && this.loginForm.password.trim().length > 0;
    },
    navbarStyle() {
      return this.scrolled
        ? 'background: rgba(0,22,33,0.96) !important; backdrop-filter: blur(14px); box-shadow: 0 4px 24px rgba(0,0,0,0.35) !important;'
        : 'background: linear-gradient(135deg, #001e2b 0%, #002633 60%, #002030 100%) !important;';
    }
  },
  async mounted() {
    await this.checkLoginStatus();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // Keep navbar in sync when login happens from another page (e.g. Invoice Payment)
    window.addEventListener('app:login', this.checkLoginStatus);
    window.addEventListener('app:logout', this.checkLoginStatus);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('app:login', this.checkLoginStatus);
    window.removeEventListener('app:logout', this.checkLoginStatus);
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 30;
    },
    async checkLoginStatus() {
      try {
        const response = await fetch(this.$api('/login/check_login.php'));
        const data = await response.json();
        this.isLoggedIn = data.message === 'loggedin';
      } catch {
        this.isLoggedIn = false;
      }
    },
    async handleLoginClick() {
      if (this.isLoggedIn) {
        try {
          const response = await fetch(this.$api('/login/logout.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await response.json();
          if (data.status === 'success') {
            this.isLoggedIn = false;
            window.dispatchEvent(new CustomEvent('app:logout'));
            this.$router.push('/');
          }
        } catch {
          this.isLoggedIn = false;
        }
      } else {
        this.loginForm = { username: '', password: '' };
        this.navLoginError = '';
        this.showPassword = false;
        this.showLoginDialog = true;
      }
    },
    async submitNavLogin() {
      this.navLoginError = '';
      if (!this.loginForm.username.trim() || !this.loginForm.password.trim()) {
        this.navLoginError = 'Please fill in all required fields.';
        return;
      }
      this.navLoginLoading = true;
      try {
        const response = await fetch(this.$api('/login/login.php'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: this.loginForm.username, password: this.loginForm.password })
        });
        const data = await response.json();
        if (data.status === 'error') {
          this.navLoginError = data.message;
        } else {
          this.isLoggedIn = true;
          this.showLoginDialog = false;
          this.loginForm = { username: '', password: '' };
          window.dispatchEvent(new CustomEvent('app:login'));
        }
      } catch {
        this.navLoginError = 'Login failed. Please try again.';
      }
      this.navLoginLoading = false;
    }
  }
};
</script>

<style scoped>
/* ── Accent bar ── */
.nb-accent-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--brand-red) 0%, #ff4444 50%, var(--brand-red) 100%);
  z-index: 1100;
}

/* ── App bar ── */
.app-navbar {
  top: 3px !important;
  transition: background 0.3s ease, box-shadow 0.3s ease !important;
  border-bottom: 1px solid rgba(255,255,255,0.07) !important;
}

/* ── Logo ── */
.nb-logo { text-decoration: none; display: flex; align-items: center; }
.nb-logo-img {
  height: 54px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.35));
  transition: transform 0.2s, filter 0.2s;
}
.nb-logo-img:hover {
  transform: translateY(-1px);
  filter: drop-shadow(0 4px 14px rgba(0,0,0,0.45));
}

/* ── Desktop nav links ── */
.nb-links { gap: 0; }

.nb-link {
  position: relative;
  color: rgba(255,255,255,0.8);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  padding: 8px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  letter-spacing: 0.3px;
  transition: color 0.2s;
}
.nb-link:hover { color: white; text-decoration: none; }
.nb-link:hover .nb-link-bar { transform: scaleX(1); }

/* Animated underline indicator */
.nb-link-bar {
  position: absolute;
  bottom: 2px;
  left: 14px;
  right: 14px;
  height: 2px;
  background: var(--brand-red);
  border-radius: 2px;
  transform: scaleX(0);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}
.nb-link--active {
  color: white !important;
}
.nb-link--active .nb-link-bar {
  transform: scaleX(1) !important;
  background: var(--brand-red);
}

/* Chevron animation */
.nb-chevron {
  transition: transform 0.25s ease;
  opacity: 0.7;
}
.nb-chevron--open { transform: rotate(180deg); opacity: 1; }

/* ── Tools Mega Menu ── */
.nb-mega {
  background: #001820;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  min-width: 480px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(94,34,29,0.15);
  overflow: hidden;
}
.nb-mega-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 14px;
  background: linear-gradient(135deg, #002633, #003a50);
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.nb-mega-header-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--brand-red), #9a0d0d);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(94,34,29,0.4);
}
.nb-mega-title {
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
}
.nb-mega-subtitle {
  color: rgba(255,255,255,0.5);
  font-size: 0.78rem;
  margin-top: 1px;
}
.nb-mega-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 12px;
}
.nb-tool-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.18s;
  border: 1px solid transparent;
}
.nb-tool-item:hover {
  background: rgba(94,34,29,0.1);
  border-color: rgba(94,34,29,0.25);
  text-decoration: none;
  transform: translateX(2px);
}
.nb-tool-icon {
  width: 38px;
  height: 38px;
  background: linear-gradient(135deg, var(--brand-red), #8a0a0a);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(94,34,29,0.35);
}
.nb-tool-name {
  color: rgba(255,255,255,0.92);
  font-size: 0.83rem;
  font-weight: 600;
  line-height: 1.2;
}
.nb-tool-desc {
  color: rgba(255,255,255,0.4);
  font-size: 0.73rem;
  margin-top: 2px;
  line-height: 1.3;
}

/* ── Divider ── */
.nb-divider {
  width: 1px;
  height: 28px;
  background: rgba(255,255,255,0.15);
  margin: 0 10px;
}

/* ── Auth button ── */
.nb-auth-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  transition: all 0.22s;
  background: linear-gradient(135deg, var(--brand-red), #9a0d0d);
  color: white;
  box-shadow: 0 3px 12px rgba(94,34,29,0.4);
}
.nb-auth-btn:hover {
  background: linear-gradient(135deg, #e01515, #b00f0f);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(94,34,29,0.55);
}
.nb-auth-btn--out {
  background: transparent;
  border: 1.5px solid rgba(255,255,255,0.3);
  color: rgba(255,255,255,0.85);
  box-shadow: none;
}
.nb-auth-btn--out:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.6);
  color: white;
  transform: translateY(-1px);
  box-shadow: none;
}
.nb-auth-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255,255,255,0.18);
  flex-shrink: 0;
}

/* ── Login Dialog (Teleport overlay) ── */
.dlg-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}
.dlg-box {
  width: 420px;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  background: transparent; /* prevents white bleed at rounded corners */
}
.dlg-fade-enter-active { transition: opacity 0.22s ease; }
.dlg-fade-leave-active { transition: opacity 0.18s ease; }
.dlg-fade-enter-from, .dlg-fade-leave-to { opacity: 0; }

.ld-header {
  background: linear-gradient(135deg, #002633 0%, #004055 55%, #5a0a0a 100%);
  padding: 28px 24px 22px;
  text-align: center;
  border-radius: 0;   /* dlg-box overflow:hidden handles clipping */
  display: flex;
  flex-direction: column;
  align-items: center;
}
.ld-icon {
  background: rgba(255,255,255,0.12);
  border-radius: 50%;
  padding: 12px;
  margin-bottom: 10px;
}
.ld-title { color: white; font-size: 1.5rem; font-weight: 800; margin-bottom: 4px; }
.ld-subtitle { color: rgba(255,255,255,0.65); font-size: 0.85rem; }
.ld-body { background: white; padding: 24px 24px 16px; display: flex; flex-direction: column; gap: 4px; }
.ld-actions { background: white; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 8px 24px 24px; }
.ld-btn {
  height: 48px; border-radius: 8px; font-size: 0.9rem; font-weight: 700;
  font-family: inherit; cursor: pointer; display: flex; align-items: center;
  justify-content: center; gap: 6px; transition: all 0.2s; border: none; letter-spacing: 0.3px;
}
.ld-btn-cancel { background: transparent; border: 2px solid #ccc; color: #555; }
.ld-btn-cancel:hover { border-color: #002633; color: #002633; background: rgba(0,38,51,0.05); }
.ld-btn-login {
  background: linear-gradient(135deg, var(--brand-red), #9a0d0d);
  color: white;
  box-shadow: 0 4px 14px rgba(94,34,29,0.35);
}
.ld-btn-login:hover:not(.ld-btn-disabled) {
  background: linear-gradient(135deg, #e01515, #b00f0f);
  box-shadow: 0 6px 20px rgba(94,34,29,0.5);
  transform: translateY(-1px);
}
.ld-btn-disabled { background: #ccc !important; color: #999 !important; cursor: not-allowed !important; box-shadow: none !important; }
.ld-spinner {
  width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Mobile dropdown panel (right-anchored, below hamburger) ── */
.dr-panel {
  position: fixed;
  top: 75px;       /* navbar (72px) + accent bar (3px) */
  right: 0;
  width: 248px;
  background: #001e2b;
  border-radius: 0 0 0 14px;
  box-shadow: -4px 8px 32px rgba(0,0,0,0.5);
  z-index: 1099;
  max-height: calc(100vh - 75px);
  overflow-y: auto;
  border-left: 1px solid rgba(255,255,255,0.07);
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.dr-slide-enter-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); }
.dr-slide-leave-active { transition: all 0.18s cubic-bezier(0.4,0,0.2,1); }
.dr-slide-enter-from,
.dr-slide-leave-to { opacity: 0; transform: translateY(-10px) scaleY(0.97); }

.dr-nav {
  padding: 10px 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.dr-section-label {
  color: rgba(255,255,255,0.3);
  font-size: 0.63rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  padding: 6px 12px 3px;
}
.dr-link {
  display: flex;
  align-items: center;
  color: rgba(255,255,255,0.78);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  padding: 9px 12px;
  border-radius: 8px;
  transition: all 0.18s;
}
.dr-link:hover { background: rgba(255,255,255,0.07); color: white; text-decoration: none; }
.dr-link--active { background: rgba(94,34,29,0.15) !important; color: #FF8080 !important; }
.dr-link-tool { font-size: 0.825rem; font-weight: 500; gap: 10px; }
.dr-tool-icon {
  width: 24px; height: 24px;
  background: linear-gradient(135deg, var(--brand-red), #8a0a0a);
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dr-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 8px 12px;
}
.dr-auth-btn {
  display: flex; align-items: center;
  padding: 10px 12px; border-radius: 8px;
  font-size: 0.875rem; font-weight: 700;
  font-family: inherit; cursor: pointer; border: none;
  background: linear-gradient(135deg, var(--brand-red), #9a0d0d);
  color: white; transition: all 0.2s;
  box-shadow: 0 3px 10px rgba(94,34,29,0.3);
}
.dr-auth-btn:hover { background: linear-gradient(135deg, #e01515, #b00f0f); }
.dr-auth-btn--out {
  background: transparent;
  border: 1.5px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.7);
  box-shadow: none;
}
.dr-auth-btn--out:hover { background: rgba(255,255,255,0.06); color: white; }
</style>
